// A trailing `Z` or an explicit `±HH:MM` / `±HHMM` offset — the only cases
// where the instant is unambiguous and safe to re-express in Eastern time.
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;

// Fixed to America/New_York so every analyst sees the same wall-clock time
// regardless of their machine's locale. MM/DD/YYYY h:mm AM/PM.
const EST_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

/**
 * Formats a BFF timestamp as `MM/DD/YYYY h:mm AM/PM` in Eastern time.
 * Only strings with an explicit timezone offset are re-expressed in Eastern
 * time; a zone-less or date-only value is rendered verbatim. Falls back to
 * the raw string if the value is empty or unparseable.
 * @param {string} raw - The raw timestamp string from the BFF.
 * @returns {string} The formatted timestamp, or the raw string on failure.
 */
export function formatDatetime(raw: string): string {
  if (!raw) return "";
  if (!HAS_TIMEZONE.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  const parts = new Map(EST_FORMAT.formatToParts(parsed).map((part) => [part.type, part.value]));
  return `${parts.get("month")}/${parts.get("day")}/${parts.get("year")} ${parts.get("hour")}:${parts.get("minute")} ${parts.get("dayPeriod")}`;
}

/**
 * Converts a SCREAMING_SNAKE_CASE code to Title Case words.
 * e.g. `PRE_APPROVAL_GRANTED` → `Pre Approval Granted`
 * @param {string} code - The snake-case code string.
 * @returns {string} The title-cased display string.
 */
export function snakeToTitleCase(code: string): string {
  return code
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
